import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { useApiClient } from '../hooks';
import { getUserStats, getRatingTier, getInitials, endpoints } from '@callbridge/ui';
import {
  SpadeSvg,
  HeartSvg,
  DiamondSvg,
  ClubSvg,
  BackArrowSvg,
  LogoutSvg,
} from '../components/SuitIcons';

interface MatchEntry {
  id: string;
  roomCode: string;
  winnerName: string;
  winnerId: string | null;
  totalRounds: number;
  durationMs: number;
  completedAt: string;
  players: Array<{
    userId: string | null;
    displayName: string;
    isAI: boolean;
    finalScore: number;
    placement: number;
  }>;
}

interface MatchesResponse {
  data: {
    matches: MatchEntry[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  };
}

interface RankResponse {
  data: {
    rank: { rank: number; total: number; percentile: number } | null;
  };
}

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const { request } = useApiClient();
  const navigate = useNavigate();

  const [recentMatches, setRecentMatches] = useState<MatchEntry[]>([]);
  const [myRank, setMyRank] = useState<{ rank: number; total: number; percentile: number } | null>(
    null,
  );
  const [loadingMatches, setLoadingMatches] = useState(false);

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoadingMatches(true);
    try {
      const [matchRes, rankRes] = await Promise.all([
        request<MatchesResponse>(endpoints.getMatches(1, 5)),
        request<RankResponse>(endpoints.getMyRank()),
      ]);
      setRecentMatches(matchRes.data.matches);
      setMyRank(rankRes.data.rank);
    } catch {
      // Non-critical
    } finally {
      setLoadingMatches(false);
    }
  }, [request, isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!user) return null;

  const stats = getUserStats(user);
  const tier = getRatingTier(user.rating);
  const initials = getInitials(user.username);

  return (
    <div className="page profile-page">
      <div className="page-bg-suits" aria-hidden="true">
        <SpadeSvg className="page-bg-icon page-bg-1" />
        <HeartSvg className="page-bg-icon page-bg-2" />
        <DiamondSvg className="page-bg-icon page-bg-3" />
        <ClubSvg className="page-bg-icon page-bg-4" />
        <SpadeSvg className="page-bg-icon page-bg-5" />
        <HeartSvg className="page-bg-icon page-bg-6" />
      </div>

      <div className="page-topbar">
        <button className="page-back" onClick={() => navigate('/')}>
          <BackArrowSvg />
          Back
        </button>
        <div className="page-topbar-right">
          {user && <span className="page-username">♠ {user.username}</span>}
          <button className="page-logout" onClick={logout}>
            <LogoutSvg />
            Logout
          </button>
        </div>
      </div>

      <div className="profile-header">
        <div className="avatar avatar-lg">{initials}</div>
        <div className="profile-header-info">
          <h1>{user.username}</h1>
          <span className={`tier-badge tier-${tier.color}`}>{tier.label}</span>
          <p className="profile-email">{user.email}</p>
        </div>
        <Link to="/settings" className="btn btn-outline btn-sm profile-settings-link">
          Settings
        </Link>
      </div>

      {/* Rating & Rank */}
      <div className="profile-rating-section">
        <div className="rating-display">
          <span className="rating-number">{stats.rating}</span>
          <span className="rating-label">Rating</span>
        </div>
        {myRank && (
          <div className="rank-display">
            <span className="rank-number">#{myRank.rank}</span>
            <span className="rank-label">Rank (Top {100 - myRank.percentile}%)</span>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="profile-stats">
        <div className="stat-card">
          <span className="stat-value">{stats.gamesPlayed}</span>
          <span className="stat-label">Games Played</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.gamesWon}</span>
          <span className="stat-label">Games Won</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.winRate}%</span>
          <span className="stat-label">Win Rate</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.gamesPlayed - stats.gamesWon}</span>
          <span className="stat-label">Games Lost</span>
        </div>
      </div>

      {/* Recent Matches */}
      <div className="profile-matches">
        <h2>Recent Matches</h2>
        {loadingMatches ? (
          <div className="loading-indicator">Loading matches...</div>
        ) : recentMatches.length === 0 ? (
          <div className="empty-state">
            <p>No matches played yet.</p>
            <Link to="/play/single" className="btn btn-primary">
              Play Now
            </Link>
          </div>
        ) : (
          <div className="match-list">
            {recentMatches.map((match) => {
              const myPlayer = match.players.find((p) => p.userId === user.id);
              const didWin = match.winnerId === user.id;
              const duration = Math.round(match.durationMs / 60000);
              return (
                <div key={match.id} className={`match-card ${didWin ? 'match-win' : 'match-loss'}`}>
                  <div className="match-result">
                    <span className={`match-badge ${didWin ? 'win' : 'loss'}`}>
                      {didWin ? 'WIN' : `#${myPlayer?.placement ?? '-'}`}
                    </span>
                  </div>
                  <div className="match-info">
                    <span className="match-score">{myPlayer?.finalScore ?? 0} pts</span>
                    <span className="match-meta">
                      {match.totalRounds} rounds · {duration}m
                    </span>
                  </div>
                  <div className="match-date">
                    {new Date(match.completedAt).toLocaleDateString()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
